import React, { useEffect, useState } from 'react';
import { Loader2, Grid3x3 } from 'lucide-react';
import { atlasApi, AtlasUser, DiscMapaItem, DiscFator, PerfilDisc } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  onAbrir: (funccodi: number) => void;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

interface Depto { codigo: number | string; nome: string; }

// hex (#rrggbb) -> rgba com a opacidade proporcional ao valor (0-100)
function celulaBg(fator: DiscFator, valor: number): string {
  const hex = DISC_CORES[fator];
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const op = Math.max(0, Math.min(1, valor / 100)) * 0.85 + 0.05;
  return `rgba(${r}, ${g}, ${b}, ${op.toFixed(2)})`;
}

function corTexto(valor: number): string {
  return valor >= 55 ? '#ffffff' : '#374151';
}

function estiloFator(estilo: string): DiscFator | null {
  const f = (estilo || '').trim().charAt(0).toUpperCase();
  return (FATORES as string[]).includes(f) ? (f as DiscFator) : null;
}

const MapaTime: React.FC<Props> = ({ user, onAbrir }) => {
  const [departamentos, setDepartamentos] = useState<Depto[]>([]);
  const [departamento, setDepartamento] = useState<string>('');
  const [itens, setItens] = useState<DiscMapaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    atlasApi.organograma(user)
      .then(r => {
        if (cancel) return;
        const deptos: Depto[] = (r.grupos || [])
          .filter((g: any) => g.centro_custo_codigo != null)
          .map((g: any) => ({ codigo: g.centro_custo_codigo, nome: g.centro_custo_nome }));
        setDepartamentos(deptos);
      })
      .catch(() => { /* dropdown opcional */ });
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getDiscMapaTime(user, departamento ? { departamento } : {})
      .then(r => { if (!cancel) setItens(r.itens || []); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar mapa do time'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, departamento]);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Grid3x3 className="w-5 h-5 text-purple-600" /> Mapa do time
        </h2>
        <select
          value={departamento}
          onChange={e => setDepartamento(e.target.value)}
          className="ml-auto px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="">Todos os departamentos</option>
          {departamentos.map(d => (
            <option key={String(d.codigo)} value={String(d.codigo)}>{d.nome}</option>
          ))}
        </select>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        {FATORES.map(f => (
          <span key={f} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: DISC_CORES[f] }} />
            {f} · {DISC_LABEL[f]}
          </span>
        ))}
        <span className="text-gray-400">Intensidade da cor = valor (0–100)</span>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...
        </div>
      ) : itens.length === 0 ? (
        <div className="p-10 text-center text-gray-400 text-sm bg-white rounded-2xl border border-gray-200">
          Ninguém respondeu o DISC neste filtro ainda.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Colaborador</th>
                <th className="px-4 py-2 text-left">Estilo</th>
                {FATORES.map(f => (
                  <th key={f} className="px-3 py-2 text-center" title={DISC_LABEL[f]}>{f}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {itens.map(p => {
                const fp = estiloFator(p.estilo_principal);
                return (
                  <tr
                    key={p.funccodi}
                    onClick={() => onAbrir(p.funccodi)}
                    className="hover:bg-purple-50/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.nome || '—'}</div>
                      {p.cargo && <div className="text-xs text-gray-400">{p.cargo}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {fp ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: DISC_CORES[fp] }}
                        >
                          {fp} · {DISC_LABEL[fp]}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    {FATORES.map(f => {
                      const v = p.perfil?.[f as keyof PerfilDisc] ?? 0;
                      return (
                        <td key={f} className="px-1 py-1 text-center">
                          <div
                            className="rounded-md py-2 text-xs font-bold"
                            style={{ backgroundColor: celulaBg(f, v), color: corTexto(v) }}
                          >
                            {v}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MapaTime;
